import React from "react";

import {
  LANDING_EGG_MOVE_MS,
  LANDING_PHOTO_FADE_IN_MS,
  LANDING_PHOTO_FADE_OUT_MS,
} from "../constants";
import { MODES } from "../urlParams";
import useEffectiveReferenceValues from "./useEffectiveReferenceValues";
import useReferenceJson from "./useReferenceJson";
import useTweenedEggTransform from "./useTweenedEggTransform";

// Single source of truth for "what marc is showing on each surface."
// Owns every marcId derivation in the app — what the player has picked,
// what the photo + onion + trace are currently rendering, what pose the
// egg should be in, and the lagged transforms each surface needs during
// cross-fades.
//
// Surfaces and their timing models:
//
//   egg pose / lighting       — `eggMarcId` + tweened `eggDisplay`. Pose
//                               tweens smoothly; lights/warmth/redshift
//                               snap (can't interpolate light arrays).
//   easel photo / onion / trace
//                             — `displayedMarcId` (lagged) +
//                               `displayedMarcTransform` (asymmetric:
//                               see below). Cross-fade via opacity.
//   scoring (MarcEngine)      — `effectiveMarcId` + `reference` (live).
//
// Three `useReferenceJson` calls on purpose:
//
//   reference         — for `effectiveMarcId`. Drives scoring + the live
//                       `eff` bag (palette + transforms used by
//                       on-canvas rendering and the showcase snapshot).
//   displayedReference — for `creative.displayedMarcId` only. Holds the
//                       OLD marc's drawing trace through the creative
//                       cross-fade so the trace src stays pinned to the
//                       OLD marc until the photo finishes fading out.
//   eggReference      — for `eggMarcId` (= landing.eggMarcId in landing,
//                       else effectiveMarcId). Drives the egg's
//                       authored transform / lights, which lag the photo
//                       on landing (photo fades in first, then egg slides).
//                       `loadedForMarcId` from this fetch is the
//                       canonical "egg has truly arrived at the round's
//                       current marc" signal that gates the speedrun
//                       count-in.
//
// The `displayedMarcTransform` asymmetry: during creative fade-out we
// read transform from `displayedEff` (lagged) so it matches the OLD
// photo. During fade-in / idle we read from `eff` (live, keyed on
// effectiveMarcId == selectedMarcId by then) — `displayedReference` is
// a microtask behind the marcId swap so its transform would briefly
// pop the photo at the wrong pose for one render before snapping.
//
// `lastDisplayMarcIdRef` is OWNED by App (declared early, read by
// useLandingCycle's `entryFromMarcId` before this hook runs) and PASSED
// IN. We keep it in sync via a useEffect that fires whenever
// logicalMarcId is non-null. Same value is used as the
// `effectiveMarcId` fallback when logicalMarcId goes null in transient
// states (speedrun pre-engine-start, creative pre-pick).
export default function useDisplayedSurfaces({
  urlState,
  mode,
  authoring,
  speedrun,
  landing,
  creative,
  lastDisplayMarcIdRef,
}) {
  const authorMode = mode === MODES.AUTHOR;
  const speedrunMode = mode === MODES.SPEEDRUN;
  const landingMode = mode === MODES.LANDING;
  const creativeMode = mode === MODES.CREATIVE;

  // The "logical" current marc — what the user has explicitly chosen
  // (or null in transitional states: speedrun pre-engine-start,
  // creative pre-pick).
  let logicalMarcId;
  if (speedrunMode) {
    logicalMarcId = speedrun.snapshot.currentMarcId;
  } else if (landingMode) {
    logicalMarcId = landing.currentId;
  } else if (creativeMode) {
    logicalMarcId = creative.selectedMarcId;
  } else {
    logicalMarcId = urlState.marcId;
  }

  // Hold the previous logical marc when in a transitional state. Lets
  // the photo + trace + scoring keep showing the marc the user was just
  // looking at instead of snapping to urlState.marcId — most visible
  // win is creative entry: picker opens and the egg/photo/trace stay
  // anchored to whatever landing was showing until the user picks.
  // Updated via useEffect (not during render) so the ref stays
  // consistent with React's commit cycle.
  React.useEffect(() => {
    if (logicalMarcId != null) {
      lastDisplayMarcIdRef.current = logicalMarcId;
    }
  }, [logicalMarcId, lastDisplayMarcIdRef]);
  const effectiveMarcId =
    logicalMarcId ?? lastDisplayMarcIdRef.current ?? urlState.marcId;

  const { reference } = useReferenceJson(effectiveMarcId, authorMode);
  const eff = useEffectiveReferenceValues({
    authorMode,
    reference,
    author: authoring,
  });

  // Reseed creative's tweakables (warmth/redshift/lights/palette) to
  // whatever the marc's author tuned, every time the user picks a new
  // marc. The user's edits then deviate from those starting points
  // until the next marc swap. Palette reseed doesn't recolor existing
  // strokes — DrawingEngine's freezeStrokeColor mode bakes color at
  // commit-time, so a swatch swap only affects future strokes.
  const creativeMarcId = creativeMode ? creative.selectedMarcId : null;
  const setCreativeWarmth = creative.setWarmth;
  const setCreativeRedshift = creative.setRedshift;
  const setCreativeEggLights = creative.setEggLights;
  const setCreativePalette = creative.setPalette;
  React.useEffect(() => {
    if (!creativeMode) return;
    if (!reference) return;
    if (!creativeMarcId) return;
    setCreativeWarmth(reference.egg?.warmth ?? 30);
    setCreativeRedshift(reference.egg?.redshift ?? 30);
    setCreativeEggLights(reference.egg?.lights ?? []);
    if (reference.palette?.length === 6) setCreativePalette(reference.palette);
  }, [
    creativeMode,
    creativeMarcId,
    reference,
    setCreativeWarmth,
    setCreativeRedshift,
    setCreativeEggLights,
    setCreativePalette,
  ]);

  // Creative cross-fade: easel + onion lag selectedMarcId by FADE_OUT_MS
  // so the marcId/transform jump on those surfaces happens under cover
  // of opacity 0. Egg side still tracks selectedMarcId immediately (so
  // the egg pose tween begins on click) — only the marc photo + onion
  // lag.
  //
  // We fetch displayedReference whenever creative.displayedMarcId is
  // non-null, INCLUDING from landing mode (when the picker is open
  // and the seed effect below has primed displayedMarcId to the
  // currently-visible marc). This is what lets the fetch resolve
  // BEFORE the user picks, so the first creative render after pick
  // already has displayedReference loaded for the OLD marc — without
  // it the photo briefly snaps to DEFAULT_MARC_TRANSFORM during the
  // first render of the fade-out.
  const { reference: displayedReference } = useReferenceJson(
    creative.displayedMarcId,
    authorMode,
  );
  const displayedEff = useEffectiveReferenceValues({
    authorMode,
    reference: displayedReference,
    author: authoring,
  });

  // Keep creative.displayedMarcId in sync with whatever's currently on
  // screen WHILE THE PICKER IS OPEN — including from landing mode,
  // before the user has picked. Two reasons:
  //   1. Lets useReferenceJson(creative.displayedMarcId) start fetching
  //      the ambient reference *before* the user picks, so the first
  //      pick's fade-out doesn't briefly read DEFAULT_MARC_TRANSFORM
  //      while displayedReference is mid-resolve. This is load-bearing
  //      now that picker opens BEFORE mode flips to creative — the old
  //      "seed on creative entry" effect ran too late, since pickMarc
  //      and setMode(CREATIVE) batch into one render and useReferenceJson
  //      hasn't resolved its first fetch yet.
  //   2. Re-syncs if the user dismisses the picker, the landing reel
  //      cycles to a new marc, then the user re-opens the picker —
  //      otherwise displayedMarcId would still point at the marc the
  //      first picker-open captured, and pickMarc would fade FROM the
  //      wrong marc.
  // Outside the picker, displayedMarcId is owned by useCreative (the
  // pickMarc cross-fade flips it from FROM-marc to TO-marc mid-fade).
  // Skip mid-cross-fade — if the user opens "Change Marc" mid-fade,
  // displayedMarcId is the FROM/TO marc of the in-flight fade and
  // forcibly snapping it to effectiveMarcId (= selectedMarcId, the
  // TO-marc) would break the fade. pickMarc will reset things if the
  // user picks a new marc anyway.
  const setCreativeDisplayedMarcId = creative.setDisplayedMarcId;
  React.useEffect(() => {
    if (!creative.pickerOpen) return;
    if (creative.transitionPhase !== "idle") return;
    if (!effectiveMarcId) return;
    if (creative.displayedMarcId === effectiveMarcId) return;
    setCreativeDisplayedMarcId(effectiveMarcId);
  }, [
    creative.pickerOpen,
    creative.transitionPhase,
    creative.displayedMarcId,
    effectiveMarcId,
    setCreativeDisplayedMarcId,
  ]);

  // Pre-pick (creative.displayedMarcId == null) we fall back to the
  // hold-previous effectiveMarcId so the easel keeps showing whatever
  // landing was rendering until the user picks. After the first pick,
  // displayedMarcId is the source of truth for the easel + onion.
  const usingCreativeDisplayed =
    creativeMode && creative.displayedMarcId != null;
  const displayedMarcId = usingCreativeDisplayed
    ? creative.displayedMarcId
    : effectiveMarcId;

  // displayedMarcTransform follows displayedEff ONLY during fade-out —
  // that's when displayedMarcId points to the OLD marc (lagged) and we
  // genuinely need the OLD reference's transform to keep the photo
  // aligned at its prior pose while it fades to opacity 0.
  //
  // During fade-in (and idle), displayedMarcId has already swapped to
  // the new marc; we read from `eff` (which has been fetching the new
  // reference since the pick fired, so it's resolved by the time the
  // 600ms fade-out finishes) instead of `displayedEff`. Otherwise the
  // displayedReference fetch is a microtask behind the marcId swap and
  // the photo fades in at the OLD transform for one render before the
  // new transform pops into place — the user-reported "transform
  // applies after fade-in" artifact.
  const displayedMarcTransform =
    creativeMode && creative.transitionPhase === "out"
      ? displayedEff.marcTransform
      : eff.marcTransform;

  // Creative mode photo + onion opacity dampening, derived from the
  // useCreative state machine. During 'out' the easel hides its photo
  // (its CSS opacity transition fades it to 0) and the onion is forced
  // to 0; during 'in' the onion's CSS transition fades it back up to
  // the user's slider value. Outside 'out'/'in' the onion has its
  // transition disabled so the slider stays responsive.
  const creativePhotoVisible =
    !creativeMode || creative.transitionPhase !== "out";
  const creativeOnionOpacity =
    creativeMode && creative.transitionPhase === "out"
      ? 0
      : creative.onionOpacity;
  const creativeOnionTransitionMs =
    creative.transitionPhase === "out"
      ? LANDING_PHOTO_FADE_OUT_MS
      : creative.transitionPhase === "in"
        ? LANDING_PHOTO_FADE_IN_MS
        : 0;

  // The egg side (pose + lighting + tints) tracks `effectiveMarcId`
  // everywhere except landing — which staggers it via `landing.eggMarcId`
  // (lagged by PHOTO_FADE_IN_MS) so the photo fades in first while the
  // egg holds the old marc's pose, then slides over. The hold-previous
  // semantic in `effectiveMarcId` already handles the no-pick states
  // (speedrun pre-start, creative pre-pick) so eggMarcId stays pinned
  // to the prior marc's id and the tween doesn't fire a stray hop.
  const eggMarcId = landingMode ? landing.eggMarcId : effectiveMarcId;
  const {
    reference: eggReference,
    loadedForMarcId: eggReferenceLoadedForMarcId,
  } = useReferenceJson(eggMarcId, authorMode);
  const eggSide = useEffectiveReferenceValues({
    authorMode,
    reference: eggReference,
    author: authoring,
  });

  // Tween the egg transform on marc change. Active in modes where the
  // marc swaps via state (landing reel, speedrun lineup advance,
  // creative pick); not in author/label, where the transform is driven
  // by sliders/drag and a tween would fight every input. `tween.tweening`
  // gates speedrun input + scoring so the player can't draw onto a
  // moving egg or score against a marc that's still flying in.
  const tweenActive = landingMode || speedrunMode || creativeMode;
  const tween = useTweenedEggTransform({
    target: eggSide.eggTransform,
    durationMs: LANDING_EGG_MOVE_MS,
    active: tweenActive,
  });

  // Trace overlay shown on the egg canvas in landing (cycle-driven
  // fade in/out) and creative (mounted at opacity 0 so the post-pick
  // fade-out animates smoothly from whatever opacity it was at). Hidden
  // in speedrun/author. Returns a finished {src, visible}|null so JSX
  // consumers don't have to rebuild the conditional.
  //
  // Source ref (`drawingRef`) is the LAGGED `displayedReference` in
  // creative so the trace src stays pinned to the OLD marc through the
  // pick cross-fade — matches the photo's `displayedMarcTransform`
  // lag. Without lag, the trace's src would flip to the picked marc
  // the instant the new fetch resolves, which mid-fade-out would
  // flash the picked marc's drawing at high opacity before the fade
  // completes. In landing the cycle owns marcId timing directly, so
  // plain `reference` is correct.
  const drawingRef = creativeMode ? displayedReference : reference;
  const landingDrawing =
    drawingRef?.drawing?.image && (landingMode || creativeMode)
      ? {
          src: drawingRef.drawing.image,
          visible: landingMode
            ? landing.drawingShown
            : !creative.selectedMarcId,
        }
      : null;

  // Creative mode overrides lights/warmth/redshift with session
  // tweakables (LightingEditor + sliders); reference values flow
  // through unchanged in every other mode.
  const eggDisplay = {
    eggTransform: tween.current,
    eggLights: creativeMode ? creative.eggLights : eggSide.eggLights,
    eggWarmth: creativeMode ? creative.warmth : eggSide.eggWarmth,
    eggRedshift: creativeMode ? creative.redshift : eggSide.eggRedshift,
  };

  // Creative owns its own palette (seeded from reference, edited via
  // double-click swatches). Other modes pass through the reference's
  // palette as `eff` already resolves it.
  const effOut = creativeMode ? { ...eff, palette: creative.palette } : eff;

  return {
    effectiveMarcId,
    displayedMarcId,
    displayedMarcTransform,
    reference,
    eff: effOut,
    landingDrawing,
    eggDisplay,
    eggReferenceLoadedForMarcId,
    tween,
    creativePhotoVisible,
    creativeOnionOpacity,
    creativeOnionTransitionMs,
  };
}
